import { Link } from "react-router-dom";
import {
  ShoppingCart,
  BarChart3,
  Smartphone,
  TrendingUp,
  Receipt,
  DollarSign,
  ArrowRight,
  CheckCircle,
  Zap,
  Shield,
  Clock,
} from "lucide-react";
import { canSuggestPWAInstallation } from "../utils/usePWAInstall";
import { TrackLink } from "../components/TrackLink";

export const Home = () => {
  const loginPath = canSuggestPWAInstallation()
    ? "/dash/pre-login"
    : "/dash/login";

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <header className="container mx-auto px-4 py-6">
        <nav className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-600 rounded-xl">
              <ShoppingCart className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">MyMarket</h1>
          </div>
          <TrackLink
            event="landing_login"
            to={loginPath}
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
          >
            Entrar
            <ArrowRight className="h-4 w-4" />
          </TrackLink>
        </nav>
      </header>

      <main className="container mx-auto px-4 py-16">
        <div className="text-center max-w-4xl mx-auto mb-16">
          <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Entenda seus gastos do
            <span className="text-blue-600 block">supermercado</span>
          </h2>
          <p className="text-xl text-gray-600 mb-8 leading-relaxed">
            Rastreie cupons fiscais, acompanhe a evolução dos gastos
            <br />e tome decisões inteligentes sobre suas compras no mercado.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <TrackLink
              event="landing_header_start"
              to={loginPath}
              className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white text-lg font-semibold rounded-xl shadow-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 transform hover:scale-105"
            >
              <Zap className="h-5 w-5" />
              Começar Agora
            </TrackLink>
            <button className="inline-flex items-center justify-center gap-3 px-8 py-4 border-2 border-gray-300 text-gray-700 text-lg font-semibold rounded-xl hover:border-blue-300 hover:text-blue-600 transition-colors">
              <Receipt className="h-5 w-5" />
              Ver Demonstração
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-20">
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
            <div className="p-3 bg-green-100 rounded-xl w-fit mb-6">
              <Smartphone className="h-8 w-8 text-green-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Escaneamento Fácil
            </h3>
            <p className="text-gray-600 leading-relaxed">
              Escaneie o QR Code do seu cupom fiscal em segundos e importe
              automaticamente todos os produtos e valores da sua compra.
            </p>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
            <div className="p-3 bg-blue-100 rounded-xl w-fit mb-6">
              <BarChart3 className="h-8 w-8 text-blue-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Análises Inteligentes
            </h3>
            <p className="text-gray-600 leading-relaxed">
              Visualize gráficos detalhados dos seus gastos, médias diárias e
              semanais, e identifique padrões nos seus hábitos de compra.
            </p>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
            <div className="p-3 bg-purple-100 rounded-xl w-fit mb-6">
              <TrendingUp className="h-8 w-8 text-purple-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Controle Financeiro
            </h3>
            <p className="text-gray-600 leading-relaxed">
              Monitore seus gastos mensais, estabeleça metas de economia e tenha
              controle total sobre seu orçamento familiar.
            </p>
          </div>
        </div>

        <div className="bg-gray-50 rounded-3xl p-12 mb-20">
          <div className="text-center mb-12">
            <h3 className="text-4xl font-bold text-gray-900 mb-4">
              Como Funciona
            </h3>
            <p className="text-xl text-gray-600">
              Apenas 3 passos simples para começar
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-blue-600 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                1
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">
                Escaneie o Cupom
              </h4>
              <p className="text-gray-600">
                Use a câmera do seu celular para escanear o QR Code do cupom
                fiscal
              </p>
            </div>

            <div className="text-center">
              <div className="bg-blue-600 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                2
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">
                Dados Importados
              </h4>
              <p className="text-gray-600">
                Todos os produtos, preços e informações são importados
                automaticamente
              </p>
            </div>

            <div className="text-center">
              <div className="bg-blue-600 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                3
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">
                Visualize Relatórios
              </h4>
              <p className="text-gray-600">
                Acesse gráficos e estatísticas detalhadas dos seus gastos
              </p>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-12 items-center mb-20">
          <div>
            <h3 className="text-4xl font-bold text-gray-900 mb-6">
              Por que escolher o MyMarket?
            </h3>
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0" />
                <span className="text-lg text-gray-700">
                  Comece a acompanhar seus gastos gratuitamente
                </span>
              </div>
              <div className="flex items-center gap-4">
                <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0" />
                <span className="text-lg text-gray-700">
                  Dados seguros e criptografados
                </span>
              </div>
              <div className="flex items-center gap-4">
                <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0" />
                <span className="text-lg text-gray-700">
                  Interface simples e intuitiva
                </span>
              </div>
              <div className="flex items-center gap-4">
                <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0" />
                <span className="text-lg text-gray-700">
                  Relatórios em tempo real
                </span>
              </div>
              <div className="flex items-center gap-4">
                <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0" />
                <span className="text-lg text-gray-700">
                  Acesso em qualquer dispositivo
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-green-50 p-6 rounded-2xl text-center">
              <DollarSign className="h-12 w-12 text-green-600 mx-auto mb-3" />
              <div className="text-2xl font-bold text-gray-900">R$ 0</div>
              <div className="text-sm text-gray-600">Comece gratuitamente</div>
            </div>
            <div className="bg-blue-50 p-6 rounded-2xl text-center">
              <Shield className="h-12 w-12 text-blue-600 mx-auto mb-3" />
              <div className="text-2xl font-bold text-gray-900">100%</div>
              <div className="text-sm text-gray-600">Dados seguros</div>
            </div>
            <div className="bg-purple-50 p-6 rounded-2xl text-center">
              <Clock className="h-12 w-12 text-purple-600 mx-auto mb-3" />
              <div className="text-2xl font-bold text-gray-900">&lt; 30s</div>
              <div className="text-sm text-gray-600">Para escanear</div>
            </div>
            <div className="bg-orange-50 p-6 rounded-2xl text-center">
              <Zap className="h-12 w-12 text-orange-600 mx-auto mb-3" />
              <div className="text-2xl font-bold text-gray-900">
                Instantâneo
              </div>
              <div className="text-sm text-gray-600">Relatórios</div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-3xl p-12 text-center text-white">
          <h3 className="text-4xl font-bold mb-4">
            Pronto para ter controle total?
          </h3>
          <p className="text-xl mb-8 text-blue-100">
            Comece hoje mesmo a organizar suas finanças familiares
          </p>
          <Link
            to={loginPath}
            className="inline-flex items-center gap-3 px-8 py-4 bg-white text-blue-600 text-lg font-semibold rounded-xl hover:bg-gray-100 transition-colors transform hover:scale-105"
          >
            <ShoppingCart className="h-5 w-5" />
            Começar Gratuitamente
            <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </main>

      <footer className="container mx-auto px-4 py-8 border-t border-gray-200 mt-20">
        <div className="text-center text-gray-600">
          <p>
            &copy; 2025 MyMarket. Desenvolvido com ❤️ para ajudar famílias
            brasileiras.
          </p>
        </div>
      </footer>
    </div>
  );
};
